import { DeleteRounded } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, IconButton, MenuItem, Select, Stack, Typography } from "@mui/material";
import { getColDefaultValue } from "components/PimDashboard/data";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { GlobalState } from "context/GlobalContext";
import { arrType, inputTypes, selectOptionsKeyName } from "data/globalData";
import { useEffect, useState } from "react";

const FilterModal = ({
	modal,
	handleCloseModal,
	filterColumns,
	setFilterColumns,
	filterColumnVal,
	setFilterColumnVal,
	localStorageStr,
	allColumns,
}) => {
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	const [filterColumnsState, setFilterColumnsState] = useState(filterColumns ?? []);
	const [filterValuesState, setFilterValuesState] = useState(filterColumnVal ?? {});
	const [attrToAdd, setAttrToAdd] = useState(inputTypes.SELECT.key);

	useEffect(() => {
		setFilterColumnsState(filterColumns ?? []);
		setFilterValuesState(filterColumnVal ?? {});
	}, [modal, filterColumns, filterColumnVal]);

	const handleClose = () => {
		handleCloseModal();
	};

	const handleClearFilterClick = () => {
		setFilterColumnsState([]);
		setFilterValuesState({});
		setFilterColumns([]);
		setFilterColumnVal({});
		localStorage.removeItem(localStorageStr);
		handleClose();
	};

	const handleColumnAddClick = () => {
		const attr = allColumns?.find((row) => row.key === attrToAdd);
		if (!attr?.key) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Column not found",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const nextAttr = { ...attr };
		if (nextAttr.input_type === inputTypes.SELECT.key) {
			nextAttr.input_type = inputTypes.MULTIPLE_SELECT.key;
		}

		setFilterValuesState({
			...filterValuesState,
			[nextAttr.key]: getColDefaultValue(nextAttr),
		});
		setFilterColumnsState([...filterColumnsState, nextAttr]);
		setAttrToAdd(inputTypes.SELECT.key);
	};

	const handleAttrDelete = (row) => {
		setFilterColumnsState(filterColumnsState.filter((col) => row.key !== col.key));
		setFilterValuesState((prev) => {
			const next = { ...prev };
			delete next[row.key];
			return next;
		});
	};

	const handleApplyClick = () => {
		if (!filterColumnsState?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No conditions to apply",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setFilterColumns(
			filterColumnsState.reduce((acc, row) => {
				if (
					filterValuesState[row.key] !== undefined &&
					filterValuesState[row.key] !== null &&
					filterValuesState[row.key] !== "" &&
					!(Array.isArray(filterValuesState[row.key]) && filterValuesState[row.key].length === 0)
				) {
					acc.push(row);
				}
				return acc;
			}, []),
		);

		const obj = { ...filterValuesState };
		Object.keys(obj).forEach((key) => {
			if (obj[key] === undefined || obj[key] === null || obj[key] === "" || (Array.isArray(obj[key]) && obj[key].length === 0)) {
				delete obj[key];
			}
		});
		setFilterColumnVal(obj);
		localStorage.setItem(localStorageStr, JSON.stringify(obj));
		handleCloseModal();
	};

	return (
		<Dialog fullWidth maxWidth="sm" open={modal} onClose={handleClose}>
			<Box width="95%" margin="auto" pt={2} display="flex" flexWrap="wrap" columnGap={2} rowGap={1}>
				<Typography width="100%" variant="h6">
					Filter
				</Typography>

				<Stack width="100%" spacing={2}>
					<Stack width={300}>
						<Typography>Add Column</Typography>
						<Box display="flex" columnGap={1} width="100%">
							<Select
								value={attrToAdd}
								onChange={(e) => setAttrToAdd(e.target.value)}
								fullWidth
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
								size="small">
								<MenuItem value={inputTypes.SELECT.key}>Select</MenuItem>
								{allColumns?.map((row) => (
									<MenuItem value={row.key} key={row.key}>
										{row.name}
									</MenuItem>
								))}
							</Select>
							<Button variant="contained" onClick={handleColumnAddClick}>
								Add
							</Button>
						</Box>
					</Stack>

					<Stack width="100%" columnGap={1} rowGap={1} display="flex" flexWrap="wrap">
						{filterColumnsState?.map((originalRow, index) => {
							const row = { ...originalRow };
							let options = {};

							if (row.input_type === inputTypes.SELECT.key || row.input_type === inputTypes.MULTIPLE_SELECT.key) {
								row.input_type = inputTypes.MULTIPLE_SELECT.key;
								options = row[selectOptionsKeyName] ?? { type: arrType, list: [] };
							}

							if (row.input_type === inputTypes.AUTO_GENERATE.key) {
								row.input_type = inputTypes.ENTER.key;
							}

							return (
								<Box key={index} display="flex" alignItems="center" width="100%">
									<RenderInputField
										width="90%"
										longWidth="90%"
										column={{ ...row, [selectOptionsKeyName]: options }}
										data={filterValuesState}
										setData={setFilterValuesState}
										selectOptObjName={selectOptionsKeyName}
										multiple={Boolean(options?.list?.length)}
									/>
									<IconButton onClick={() => handleAttrDelete(row)}>
										<DeleteRounded />
									</IconButton>
								</Box>
							);
						})}
					</Stack>
				</Stack>

				<DialogActions sx={{ marginLeft: "auto" }}>
					<Button variant="outlined" onClick={handleClose}>
						Cancel
					</Button>
					<Button variant="outlined" onClick={handleClearFilterClick}>
						Remove Filter
					</Button>
					<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={handleApplyClick} disabled={filterColumnsState?.length === 0}>
						Apply Filter
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default FilterModal;
